import { supabase, isSupabaseConfigured } from './supabaseClient';

export type AuthStatus =
  | { status: 'signed_in' }
  | { status: 'needs_email_verification'; email: string }
  | { status: 'error'; message: string };

function normalizeAuthErrorMessage(err: any): string {
  const msg = (err?.message || '').toString();
  if (!msg) return 'Authentication failed';

  // Supabase errors may include low-level fetch/body read messages; make them user-friendly.
  if (msg.toLowerCase().includes('body stream already read')) {
    return 'Signup failed due to a network/redirect configuration issue. Please try again after confirming your Supabase Auth redirect URLs and email settings.';
  }

  return msg;
}

function isEmailNotConfirmedError(err: any): boolean {
  const msg = (err?.message || '').toString().toLowerCase();
  return msg.includes('email not confirmed') || msg.includes('email_not_confirmed');
}

export async function signInWithPassword(email: string, password: string): Promise<AuthStatus> {
  if (!isSupabaseConfigured()) {
    return { status: 'error', message: 'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.' };
  }

  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (isEmailNotConfirmedError(error)) {
        return { status: 'needs_email_verification', email };
      }
      return { status: 'error', message: normalizeAuthErrorMessage(error) };
    }

    if (!data.session) {
      // Some auth configurations may not return a session (rare). Treat as verification-required.
      return { status: 'needs_email_verification', email };
    }

    return { status: 'signed_in' };
  } catch (e: any) {
    return { status: 'error', message: normalizeAuthErrorMessage(e) };
  }
}

export async function signUpWithEmail(email: string, password: string, fullName: string): Promise<AuthStatus> {
  if (!isSupabaseConfigured()) {
    return { status: 'error', message: 'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.' };
  }

  try {
    const redirectTo = window.location.origin;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: redirectTo,
      },
    });

    if (error) return { status: 'error', message: normalizeAuthErrorMessage(error) };

    // With email confirmations enabled, Supabase returns user but usually no session.
    if (!data.session) return { status: 'needs_email_verification', email };

    return { status: 'signed_in' };
  } catch (e: any) {
    return { status: 'error', message: normalizeAuthErrorMessage(e) };
  }
}

export async function resendSignupConfirmation(email: string): Promise<AuthStatus> {
  if (!isSupabaseConfigured()) {
    return { status: 'error', message: 'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.' };
  }

  try {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: window.location.origin },
    });

    if (error) return { status: 'error', message: normalizeAuthErrorMessage(error) };

    return { status: 'needs_email_verification', email };
  } catch (e: any) {
    return { status: 'error', message: normalizeAuthErrorMessage(e) };
  }
}
